/**
 * Authentication cookie synchronization utilities
 * Syncs OIDC tokens from localStorage to cookies for middleware access
 */

// Authentication cookie names (must match middleware.ts)
export const AUTH_COOKIES = {
  evcoreToken: "auth-token-evcore",
  evbasicToken: "auth-token-evbasic",
  realm: "auth-realm",
} as const;

// OIDC localStorage keys
const OIDC_KEYS = {
  evcore: `oidc.user:${process.env.NEXT_PUBLIC_KEYCLOAK_URL}/realms/evcore:ev-candidate`,
  evbasic: `oidc.user:${process.env.NEXT_PUBLIC_KEYCLOAK_URL}/realms/evbasic:ev-candidate`,
} as const;

/**
 * Set authentication cookies
 */
function setAuthCookie(name: string, value: string, expiresIn: number): void {
  const expires = new Date(Date.now() + expiresIn * 1000);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * Clear authentication cookies
 */
function clearAuthCookies(): void {
  document.cookie = `${AUTH_COOKIES.evcoreToken}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  document.cookie = `${AUTH_COOKIES.evbasicToken}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  document.cookie = `${AUTH_COOKIES.realm}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Sync tokens from localStorage to cookies for middleware access
 */
export function syncTokensToMiddleware(): void {
  try {
    // Get current realm from persisted store
    const persistedStore = localStorage.getItem("persisted-bound-store");
    const currentRealm = persistedStore ? JSON.parse(persistedStore)?.state?.realm : "evcore";

    let tokenSynced = false;

    // Check evcore token
    const evcoreData = localStorage.getItem(OIDC_KEYS.evcore);
    if (evcoreData) {
      const userData = JSON.parse(evcoreData);
      if (userData.access_token && userData.expires_at) {
        const now = Math.floor(Date.now() / 1000);
        const expiresIn = userData.expires_at - now;

        if (expiresIn > 0) {
          setAuthCookie(AUTH_COOKIES.evcoreToken, userData.access_token, expiresIn);
          if (currentRealm === "evcore") {
            setAuthCookie(AUTH_COOKIES.realm, "evcore", expiresIn);
            tokenSynced = true;
          }
        }
      }
    }

    // Check evbasic token
    const evbasicData = localStorage.getItem(OIDC_KEYS.evbasic);
    if (evbasicData) {
      const userData = JSON.parse(evbasicData);
      if (userData.access_token && userData.expires_at) {
        const now = Math.floor(Date.now() / 1000);
        const expiresIn = userData.expires_at - now;

        if (expiresIn > 0) {
          setAuthCookie(AUTH_COOKIES.evbasicToken, userData.access_token, expiresIn);
          if (currentRealm === "evbasic") {
            setAuthCookie(AUTH_COOKIES.realm, "evbasic", expiresIn);
            tokenSynced = true;
          }
        }
      }
    }

    // If no valid token was synced, clear cookies
    if (!tokenSynced) {
      clearAuthCookies();
    }
  } catch (_error) {
    clearAuthCookies();
  }
}

/**
 * Initialize token synchronization on app start
 */
export function initializeAuthSync(): void {
  // Only run in browser
  if (typeof window === "undefined") {
    return;
  }

  // Sync tokens immediately
  syncTokensToMiddleware();

  // Set up periodic sync (every 30 seconds)
  setInterval(syncTokensToMiddleware, 30000);

  // Sync on storage changes (when tokens are updated)
  window.addEventListener("storage", (event) => {
    if (event.key?.includes("oidc.user") || event.key === "persisted-bound-store") {
      syncTokensToMiddleware();
    }
  });

  // Sync on focus (when user returns to tab)
  window.addEventListener("focus", syncTokensToMiddleware);

  // Sync on page visibility change
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      syncTokensToMiddleware();
    }
  });
}
